import {robustClientFetch} from "@vesta/error-handling-remix/fetches/robustClientFetch";
import {jsonValidator} from "@vesta/global-common-typescript/validators/zodValidators";
import {z as zod} from "zod";

const minervaDatasetValidator = zod.object({
    agentContext: zod
        .string()
        .nullable()
        .transform((value) => value ?? ""),
    description: zod
        .string()
        .nullable()
        .transform((value) => value ?? ""),
    displayName: zod.string(),
    healthStatus: zod.string().uuid(),
    status: zod.string().uuid(),
    id: zod.string().uuid(),
});

const minervaDatasetPageValidator = zod.object({
    items: zod.array(minervaDatasetValidator),
    limit: zod.number(),
    offset: zod.number(),
    total: zod.number(),
});

export type MinervaDataset = zod.infer<typeof minervaDatasetValidator>;

/** Lists all datasets known to Minerva for an admin-selected workspace. */
export async function fetchMinervaWorkspaceDatasets({
    accessToken,
    intellsysBaseUrl,
    workspaceId,
    includeArchived = false,
}: {
    accessToken: string;
    intellsysBaseUrl: string;
    workspaceId: string;
    includeArchived?: boolean;
}): Promise<Array<MinervaDataset>> {
    const datasets: Array<MinervaDataset> = [];
    const statuses = ["7a398170-70be-4ce7-b271-13f58cf6cec8", ...(includeArchived ? ["d8166e19-8131-4f37-8aa6-0853e62eae90"] : [])];
    for (const status of statuses) {
        let offset = 0;
        while (true) {
            const result = await robustClientFetch(
                `${intellsysBaseUrl}/sub-system/minerva/workspaces/${encodeURIComponent(workspaceId)}/datasets?status=${status}&limit=200&offset=${offset}`,
                {headers: {Authorization: `Bearer ${accessToken}`}},
                jsonValidator.pipe(minervaDatasetPageValidator),
            );
            if (result.success !== true) {
                throw result.error;
            }
            datasets.push(...result.data.items);
            offset += result.data.items.length;
            if (offset >= result.data.total || result.data.items.length === 0) {
                break;
            }
        }
    }
    return datasets;
}
